import { ISelectOption } from 'app/interfaces/ISelectOption';
import moment from 'moment-timezone';
import { isDate } from 'lodash';

export interface ITimeZone {
    value: string;
    text: string;
    utc: string[];
    offset: number;
    isdst: boolean;
    dstSouth?: boolean; // these countries obvserve DST during south hemisphere summer (north hemisphere winter)
    abbr: string;
}

/* eslint-disable @typescript-eslint/no-use-before-define */
export function guessTimezone(): string {
    const timezone = getGuessTimeZone();
    if (timezone) {
        return timezone.utc[0];
    }

    // return UTC if we didn't recognize it
    const utcTimezone = timezones.find((v) => v.value === 'UTC');
    return utcTimezone.utc[0];
}

export function guessTimezoneValue(): string {
    const timezone = getGuessTimeZone();
    if (timezone) {
        return timezone.value;
    }

    // return UTC if we didn't recognize it
    const utcTimezone = timezones.find((v) => v.value === 'UTC');
    return utcTimezone.utc[0];
}

export function guessTimezoneAbbr(): string {
    const timezone = getGuessTimeZone();
    if (timezone) {
        return timezone.abbr;
    }
    return '';
}

export function getAllTimeZonesIanaFormat(): Array<ISelectOption<string>> {
    const ianaTimeZones: Array<ISelectOption<string>> = [];
    timezones.forEach((zone) => {
        const dstInEffect = isDaylightSavingsInEffect(zone);
        zone.utc.forEach((tz) => {
            if (!/\d/.test(tz)) { // IANA timezone doesn't contain numbers, such as 'Etc/GMT+12'
                const offset = zone.isdst && !dstInEffect ? zone.offset - 1 : zone.offset;
                let label = tz;
                label += offset > 0 ? ` (GMT+${offset})` : (offset < 0 ? ` (GMT${offset})` : '  (GMT)');
                ianaTimeZones.push({
                    label,
                    value: tz
                });
            }
        });
    });
    return ianaTimeZones;
}

// Fixed offset references offset during DST. Adjust when DST is not in effect
export function getAllTimeZonesConsiderDST(): ITimeZone[] {
    return timezones.map((zone) => {
        if (zone.text && zone.isdst) {
            const dstInEffect = isDaylightSavingsInEffect(zone);
            const zoneText = zone.text.split(' (UTC')[0];
            const newOffset = dstInEffect ? zone.offset : zone.offset - 1;
            zone.text = newOffset < 0 ? `${zoneText} (UTC${newOffset})` : `${zoneText} (UTC+${newOffset})`;
        }
        return zone;
    });
}

export function isTodayNorthHemisphereSummer(): boolean {
    const today = new Date();
    return today.getMonth() >= 4 && today.getMonth() < 11;
}

export function isTodaySouthHemisphereSummer(): boolean {
    const today = new Date();
    return today.getMonth() >= 10 || today.getMonth() < 4;
}

export function isDaylightSavingsInEffect(timezone: ITimeZone): boolean {
    if (timezone.isdst) {
        return timezone.dstSouth ? isTodaySouthHemisphereSummer() : isTodayNorthHemisphereSummer();
    }
    return false;
}

function getGuessTimeZone(): ITimeZone {
    const guess = moment.tz.guess();
    return timezones.find((tz) => tz.utc.includes(guess));
}

export function secondsToString(ms: number): string {
    function numberEnding(num: number): string {
        return (num > 1) ? 's' : '';
    }

    let temp = Math.floor(ms / 1000);
    const years = Math.floor(temp / 31536000);
    if (years) {
        return years + ' ' + stringResources.Time_Year + numberEnding(years);
    }
    const days = Math.floor((temp %= 31536000) / 86400);
    if (days) {
        return days + ' ' + stringResources.Time_Day + numberEnding(days);
    }
    const hours = Math.floor((temp %= 86400) / 3600);
    if (hours) {
        return hours + ' ' + stringResources.Time_Hour + numberEnding(hours);
    }
    const minutes = Math.floor((temp %= 3600) / 60);
    if (minutes) {
        return minutes + ' ' + stringResources.Time_Minute + numberEnding(minutes);
    }
    const seconds = temp % 60;
    if (seconds) {
        return seconds + ' ' + stringResources.Time_Second + numberEnding(seconds);
    }
    return ms + ' ' + stringResources.Time_Ms;
}

export function isRelativeUTCTime(timeStr: string): boolean {
    // Ex. 2020-10-05T01:30:00+01:00
    return timeStr.length >= 6 && ['+', '-'].includes(timeStr.charAt(timeStr.length - 6));
}

// isdst: true -> offset represents the UTC offset during daylight savings time
// TODO: The following timezones are missing: Eastern Standard Time (Mexico), US Eastern Standard Time, Line Islands Standard Time
// TODO: The following timezones are missing: Russia Time Zone 3, Russia Time Zone 10, Russia Time Zone 11
export const timezones: ITimeZone[] = [
    {
        value: 'Dateline Standard Time',
        abbr: 'DST',
        offset: -12,
        isdst: false,
        text: stringResources.Timezone_DST,
        utc: [
            'Etc/GMT+12'
        ]
    },
    {
        value: 'UTC-11',
        abbr: 'U',
        offset: -11,
        isdst: false,
        text: stringResources.Timezone_UTC11,
        utc: [
            'Etc/GMT+11',
            'Pacific/Midway',
            'Pacific/Niue',
            'Pacific/Pago_Pago'
        ]
    },
    {
        value: 'Hawaiian Standard Time',
        abbr: 'HST',
        offset: -10,
        isdst: false,
        text: stringResources.Timezone_Hawaii,
        utc: [
            'Etc/GMT+10',
            'Pacific/Honolulu',
            'Pacific/Johnston',
            'Pacific/Rarotonga',
            'Pacific/Tahiti'
        ]
    },
    {
        value: 'Alaskan Standard Time',
        abbr: 'AKDT',
        offset: -8,
        isdst: true,
        text: stringResources.Timezone_Alaska,
        utc: [
            'America/Anchorage',
            'America/Juneau',
            'America/Nome',
            'America/Sitka',
            'America/Yakutat'
        ]
    },
    {
        value: 'Pacific Standard Time (Mexico)',
        abbr: 'PDT',
        offset: -7,
        isdst: true,
        text: stringResources.Timezone_Baja,
        utc: [
            'America/Santa_Isabel'
        ]
    },
    {
        value: 'Pacific Standard Time',
        abbr: 'PDT',
        offset: -7,
        isdst: true,
        text: stringResources.Timezone_PST,
        utc: [
            'America/Los_Angeles',
            'America/Whitehorse',
            'America/Dawson',
            'America/Tijuana',
            'America/Vancouver',
            'PST8PDT'
        ]
    },
    {
        value: 'US Mountain Standard Time',
        abbr: 'UMST',
        offset: -7,
        isdst: false,
        text: stringResources.Timezone_Arizona,
        utc: [
            'America/Creston',
            'America/Dawson_Creek',
            'America/Hermosillo',
            'America/Phoenix',
            'Etc/GMT+7'
        ]
    },
    {
        value: 'Mountain Standard Time (Mexico)',
        abbr: 'MDT',
        offset: -6,
        isdst: true,
        text: stringResources.Timezone_Chihuahua,
        utc: [
            'America/Chihuahua',
            'America/Mazatlan'
        ]
    },
    {
        value: 'Mountain Standard Time',
        abbr: 'MDT',
        offset: -6,
        isdst: true,
        text: stringResources.Timezone_Mountain,
        utc: [
            'America/Boise',
            'America/Cambridge_Bay',
            'America/Denver',
            'America/Edmonton',
            'America/Inuvik',
            'America/Ojinaga',
            'America/Yellowknife',
            'MST7MDT'
        ]
    },
    {
        value: 'Central America Standard Time',
        abbr: 'CAST',
        offset: -6,
        isdst: false,
        text: stringResources.Timezone_CentralAmerica,
        utc: [
            'America/Belize',
            'America/Costa_Rica',
            'America/El_Salvador',
            'America/Guatemala',
            'America/Managua',
            'America/Tegucigalpa',
            'Etc/GMT+6',
            'Pacific/Galapagos'
        ]
    },
    {
        value: 'Central Standard Time',
        abbr: 'CDT',
        offset: -5,
        isdst: true,
        text: stringResources.Timezone_CST,
        utc: [
            'America/Chicago',
            'America/Indiana/Knox',
            'America/Indiana/Tell_City',
            'America/Matamoros',
            'America/Menominee',
            'America/North_Dakota/Beulah',
            'America/North_Dakota/Center',
            'America/North_Dakota/New_Salem',
            'America/Rainy_River',
            'America/Rankin_Inlet',
            'America/Resolute',
            'America/Winnipeg',
            'CST6CDT'
        ]
    },
    {
        value: 'Central Standard Time (Mexico)',
        abbr: 'CDT',
        offset: -5,
        isdst: true,
        text: stringResources.Timezone_Gaudalajara,
        utc: [
            'America/Bahia_Banderas',
            'America/Cancun',
            'America/Merida',
            'America/Mexico_City',
            'America/Monterrey'
        ]
    },
    {
        value: 'Canada Central Standard Time',
        abbr: 'CCST',
        offset: -6,
        isdst: false,
        text: stringResources.Timezone_Saskatchewan,
        utc: [
            'America/Regina',
            'America/Swift_Current'
        ]
    },
    {
        value: 'SA Pacific Standard Time',
        abbr: 'SPST',
        offset: -5,
        isdst: false,
        text: stringResources.Timezone_SPST,
        utc: [
            'America/Bogota',
            'America/Cayman',
            'America/Coral_Harbour',
            'America/Eirunepe',
            'America/Guayaquil',
            'America/Jamaica',
            'America/Lima',
            'America/Panama',
            'America/Rio_Branco',
            'Etc/GMT+5'
        ]
    },
    {
        value: 'Eastern Standard Time',
        abbr: 'EDT',
        offset: -4,
        isdst: true,
        text: stringResources.Timezone_EST,
        utc: [
            'America/Detroit',
            'America/Havana',
            'America/Indiana/Petersburg',
            'America/Indiana/Vincennes',
            'America/Indiana/Winamac',
            'America/Iqaluit',
            'America/Kentucky/Monticello',
            'America/Louisville',
            'America/Montreal',
            'America/Nassau',
            'America/New_York',
            'America/Nipigon',
            'America/Pangnirtung',
            'America/Port-au-Prince',
            'America/Thunder_Bay',
            'America/Toronto',
            'EST5EDT'
        ]
    },
    {
        value: 'Venezuela Standard Time',
        abbr: 'VST',
        offset: -4.5,
        isdst: false,
        text: stringResources.Timezone_VST,
        utc: [
            'America/Caracas'
        ]
    },
    {
        value: 'Paraguay Standard Time',
        abbr: 'PYT',
        offset: -4,
        isdst: true,
        dstSouth: true,
        text: stringResources.Timezone_PYT,
        utc: [
            'America/Asuncion'
        ]
    },
    {
        value: 'Atlantic Standard Time',
        abbr: 'ADT',
        offset: -3,
        isdst: true,
        text: stringResources.Timezone_ADT,
        utc: [
            'America/Glace_Bay',
            'America/Goose_Bay',
            'America/Halifax',
            'America/Moncton',
            'America/Thule',
            'Atlantic/Bermuda'
        ]
    },
    {
        value: 'Central Brazilian Standard Time',
        abbr: 'CBST',
        offset: -4,
        isdst: false,
        text: stringResources.Timezone_CBST,
        utc: [
            'America/Campo_Grande',
            'America/Cuiaba'
        ]
    },
    {
        value: 'SA Western Standard Time',
        abbr: 'SWST',
        offset: -4,
        isdst: false,
        text: stringResources.Timezone_SWST,
        utc: [
            'America/Anguilla',
            'America/Antigua',
            'America/Aruba',
            'America/Barbados',
            'America/Blanc-Sablon',
            'America/Boa_Vista',
            'America/Curacao',
            'America/Dominica',
            'America/Grand_Turk',
            'America/Grenada',
            'America/Guadeloupe',
            'America/Guyana',
            'America/Kralendijk',
            'America/La_Paz',
            'America/Lower_Princes',
            'America/Manaus',
            'America/Marigot',
            'America/Martinique',
            'America/Montserrat',
            'America/Port_of_Spain',
            'America/Porto_Velho',
            'America/Puerto_Rico',
            'America/Santo_Domingo',
            'America/St_Barthelemy',
            'America/St_Kitts',
            'America/St_Lucia',
            'America/St_Thomas',
            'America/St_Vincent',
            'America/Tortola',
            'Etc/GMT+4'
        ]
    },
    {
        value: 'Pacific SA Standard Time',
        abbr: 'PSST',
        offset: -4,
        isdst: false,
        text: stringResources.Timezone_PSST,
        utc: [
            'America/Santiago',
            'Antarctica/Palmer'
        ]
    },
    {
        value: 'Newfoundland Standard Time',
        abbr: 'NDT',
        offset: -2.5,
        isdst: true,
        text: stringResources.Timezone_NDT,
        utc: [
            'America/St_Johns'
        ]
    },
    {
        value: 'E. South America Standard Time',
        abbr: 'ESAST',
        offset: -3,
        isdst: false,
        text: stringResources.Timezone_ESAST,
        utc: [
            // 'America/Sao_Paulo' TODO: Brazil abolished Daylight Savings February 2019. Next moment-timezone update verify if this is fixed
            'America/Argentina/La_Rioja' // Argentina and Sao_Paulo both are UTC-3 and do not observe Daylight Savings
        ]
    },
    {
        value: 'Argentina Standard Time',
        abbr: 'AST',
        offset: -3,
        isdst: false,
        text: stringResources.Timezone_AST,
        utc: [
            'America/Argentina/La_Rioja',
            'America/Argentina/Rio_Gallegos',
            'America/Argentina/Salta',
            'America/Argentina/San_Juan',
            'America/Argentina/San_Luis',
            'America/Argentina/Tucuman',
            'America/Argentina/Ushuaia',
            'America/Buenos_Aires',
            'America/Catamarca',
            'America/Cordoba',
            'America/Jujuy',
            'America/Mendoza'
        ]
    },
    {
        value: 'SA Eastern Standard Time',
        abbr: 'SEST',
        offset: -3,
        isdst: false,
        text: stringResources.Timezone_SEST,
        utc: [
            'America/Araguaina',
            'America/Belem',
            'America/Cayenne',
            'America/Fortaleza',
            'America/Maceio',
            'America/Paramaribo',
            'America/Recife',
            'America/Santarem',
            'Antarctica/Rothera',
            'Atlantic/Stanley',
            'Etc/GMT+3'
        ]
    },
    {
        value: 'Montevideo Standard Time',
        abbr: 'MST',
        offset: -3,
        isdst: false,
        text: stringResources.Timezone_MST,
        utc: [
            'America/Montevideo'
        ]
    },
    {
        value: 'Bahia Standard Time',
        abbr: 'BST',
        offset: -3,
        isdst: false,
        text: stringResources.Timezone_BST,
        utc: [
            'America/Bahia'
        ]
    },
    {
        value: 'Greenland Standard Time',
        abbr: 'GDT',
        offset: -2,
        isdst: true,
        text: stringResources.Timezone_GDT,
        utc: [
            'America/Godthab'
        ]
    },
    {
        value: 'UTC-02',
        abbr: 'U',
        offset: -2,
        isdst: false,
        text: stringResources.Timezone_UTC2,
        utc: [
            'America/Noronha',
            'Atlantic/South_Georgia',
            'Etc/GMT+2'
        ]
    },
    {
        value: 'Mid-Atlantic Standard Time',
        abbr: 'MDT',
        offset: -1,
        isdst: true,
        text: stringResources.Timezone_MDT,
        utc: []
    },
    {
        value: 'Azores Standard Time',
        abbr: 'ADT',
        offset: 0,
        isdst: true,
        text: stringResources.Timezone_Azores,
        utc: [
            'America/Scoresbysund',
            'Atlantic/Azores'
        ]
    },
    {
        value: 'Cape Verde Standard Time',
        abbr: 'CVST',
        offset: -1,
        isdst: false,
        text: stringResources.Timezone_CVST,
        utc: [
            'Atlantic/Cape_Verde',
            'Etc/GMT+1'
        ]
    },
    {
        value: 'UTC',
        abbr: 'UTC',
        offset: 0,
        isdst: false,
        text: stringResources.Timezone_UTC,
        utc: [
            'America/Danmarkshavn',
            'Etc/GMT',
            'UTC'
        ]
    },
    {
        value: 'Morocco Standard Time',
        abbr: 'MDT',
        offset: 1,
        isdst: false, // Morocco observes DST but does not change clocks
        text: stringResources.Timezone_Casablanca,
        utc: [
            'Africa/Casablanca',
            'Africa/El_Aaiun'
        ]
    },
    {
        value: 'GMT Standard Time',
        abbr: 'GDT',
        offset: 1,
        isdst: true,
        text: stringResources.Timezone_GMT,
        utc: [
            'Atlantic/Canary',
            'Atlantic/Faeroe',
            'Atlantic/Madeira',
            'Europe/Dublin',
            'Europe/Guernsey',
            'Europe/Isle_of_Man',
            'Europe/Jersey',
            'Europe/Lisbon',
            'Europe/London'
        ]
    },
    {
        value: 'Greenwich Standard Time',
        abbr: 'GST',
        offset: 0,
        isdst: false,
        text: stringResources.Timezone_GST,
        utc: [
            'Africa/Abidjan',
            'Africa/Accra',
            'Africa/Bamako',
            'Africa/Banjul',
            'Africa/Bissau',
            'Africa/Conakry',
            'Africa/Dakar',
            'Africa/Freetown',
            'Africa/Lome',
            'Africa/Monrovia',
            'Africa/Nouakchott',
            'Africa/Ouagadougou',
            'Africa/Sao_Tome',
            'Atlantic/Reykjavik',
            'Atlantic/St_Helena'
        ]
    },
    {
        value: 'W. Europe Standard Time',
        abbr: 'WEDT',
        offset: 2,
        isdst: true,
        text: stringResources.Timezone_WEDT,
        utc: [
            'Arctic/Longyearbyen',
            'Europe/Amsterdam',
            'Europe/Andorra',
            'Europe/Berlin',
            'Europe/Busingen',
            'Europe/Gibraltar',
            'Europe/Luxembourg',
            'Europe/Malta',
            'Europe/Monaco',
            'Europe/Oslo',
            'Europe/Rome',
            'Europe/San_Marino',
            'Europe/Stockholm',
            'Europe/Vaduz',
            'Europe/Vatican',
            'Europe/Vienna',
            'Europe/Zurich'
        ]
    },
    {
        value: 'Central Europe Standard Time',
        abbr: 'CEDT',
        offset: 2,
        isdst: true,
        text: stringResources.Timezone_CEDT,
        utc: [
            'Europe/Belgrade',
            'Europe/Bratislava',
            'Europe/Budapest',
            'Europe/Ljubljana',
            'Europe/Podgorica',
            'Europe/Prague',
            'Europe/Tirane'
        ]
    },
    {
        value: 'Romance Standard Time',
        abbr: 'RDT',
        offset: 2,
        isdst: true,
        text: stringResources.Timezone_RDT,
        utc: [
            'Africa/Ceuta',
            'Europe/Brussels',
            'Europe/Copenhagen',
            'Europe/Madrid',
            'Europe/Paris'
        ]
    },
    {
        value: 'Central European Standard Time',
        abbr: 'CEDT',
        offset: 2,
        isdst: true,
        text: stringResources.Timezone_Sarajevo,
        utc: [
            'Europe/Sarajevo',
            'Europe/Skopje',
            'Europe/Warsaw',
            'Europe/Zagreb'
        ]
    },
    {
        value: 'W. Central Africa Standard Time',
        abbr: 'WCAST',
        offset: 1,
        isdst: false,
        text: stringResources.Timezone_WCAST,
        utc: [
            'Africa/Algiers',
            'Africa/Bangui',
            'Africa/Brazzaville',
            'Africa/Douala',
            'Africa/Kinshasa',
            'Africa/Lagos',
            'Africa/Libreville',
            'Africa/Luanda',
            'Africa/Malabo',
            'Africa/Ndjamena',
            'Africa/Niamey',
            'Africa/Porto-Novo',
            'Africa/Tunis',
            'Etc/GMT-1'
        ]
    },
    {
        value: 'Namibia Standard Time',
        abbr: 'NST',
        offset: 1,
        isdst: false,
        text: stringResources.Timezone_Namibia,
        utc: [
            'Africa/Windhoek'
        ]
    },
    {
        value: 'GTB Standard Time',
        abbr: 'GDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_GTB,
        utc: [
            'Asia/Nicosia',
            'Europe/Athens',
            'Europe/Bucharest',
            'Europe/Chisinau'
        ]
    },
    {
        value: 'Middle East Standard Time',
        abbr: 'MEDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_MEDT,
        utc: [
            'Asia/Beirut'
        ]
    },
    {
        value: 'Egypt Standard Time',
        abbr: 'EST',
        offset: 2,
        isdst: false,
        text: stringResources.Timezone_Egypt,
        utc: [
            'Africa/Cairo'
        ]
    },
    {
        value: 'Syria Standard Time',
        abbr: 'SDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_Syria,
        utc: [
            'Asia/Damascus'
        ]
    },
    {
        value: 'E. Europe Standard Time',
        abbr: 'EEDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_EEDT,
        utc: [
            'Asia/Nicosia',
            'Europe/Athens',
            'Europe/Bucharest',
            'Europe/Chisinau',
            'Europe/Helsinki',
            'Europe/Kiev',
            'Europe/Mariehamn',
            'Europe/Nicosia',
            'Europe/Riga',
            'Europe/Sofia',
            'Europe/Tallinn',
            'Europe/Uzhgorod',
            'Europe/Vilnius',
            'Europe/Zaporozhye'

        ]
    },
    {
        value: 'South Africa Standard Time',
        abbr: 'SAST',
        offset: 2,
        isdst: false,
        text: stringResources.Timezone_SAST,
        utc: [
            'Africa/Blantyre',
            'Africa/Bujumbura',
            'Africa/Gaborone',
            'Africa/Harare',
            'Africa/Johannesburg',
            'Africa/Kigali',
            'Africa/Lubumbashi',
            'Africa/Lusaka',
            'Africa/Maputo',
            'Africa/Maseru',
            'Africa/Mbabane',
            'Etc/GMT-2'
        ]
    },
    {
        value: 'FLE Standard Time',
        abbr: 'FDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_FDT,
        utc: [
            'Europe/Helsinki',
            'Europe/Kiev',
            'Europe/Mariehamn',
            'Europe/Riga',
            'Europe/Sofia',
            'Europe/Tallinn',
            'Europe/Uzhgorod',
            'Europe/Vilnius',
            'Europe/Zaporozhye'
        ]
    },
    {
        value: 'Turkey Standard Time',
        abbr: 'TDT',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_Turkey,
        utc: [
            'Europe/Istanbul'
        ]
    },
    {
        value: 'Israel Standard Time',
        abbr: 'JDT',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_Israel,
        utc: [
            'Asia/Jerusalem'
        ]
    },
    {
        value: 'Libya Standard Time',
        abbr: 'LST',
        offset: 2,
        isdst: false,
        text: stringResources.Timezone_Libya,
        utc: [
            'Africa/Tripoli'
        ]
    },
    {
        value: 'Jordan Standard Time',
        abbr: 'JST',
        offset: 3,
        isdst: true,
        text: stringResources.Timezone_Jordan,
        utc: [
            'Asia/Amman'
        ]
    },
    {
        value: 'Arabic Standard Time',
        abbr: 'AST',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_Arabic,
        utc: [
            'Asia/Baghdad'
        ]
    },
    {
        value: 'Kaliningrad Standard Time',
        abbr: 'KST',
        offset: 2,
        isdst: false,
        text: stringResources.Timezone_Kalinigrad,
        utc: [
            'Europe/Kaliningrad'
        ]
    },
    {
        value: 'Arab Standard Time',
        abbr: 'AST',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_Arab,
        utc: [
            'Asia/Aden',
            'Asia/Bahrain',
            'Asia/Kuwait',
            'Asia/Qatar',
            'Asia/Riyadh'
        ]
    },
    {
        value: 'E. Africa Standard Time',
        abbr: 'EAST',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_EAST,
        utc: [
            'Africa/Addis_Ababa',
            'Africa/Asmera',
            'Africa/Dar_es_Salaam',
            'Africa/Djibouti',
            'Africa/Juba',
            'Africa/Kampala',
            'Africa/Khartoum',
            'Africa/Mogadishu',
            'Africa/Nairobi',
            'Antarctica/Syowa',
            'Etc/GMT-3',
            'Indian/Antananarivo',
            'Indian/Comoro',
            'Indian/Mayotte'
        ]
    },
    {
        value: 'Belarus Standard Time',
        abbr: 'MSK',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_Minsk,
        utc: [
            'Europe/Minsk'
        ]
    },
    {
        value: 'Russian Standard Time',
        abbr: 'MSK',
        offset: 3,
        isdst: false,
        text: stringResources.Timezone_MSK,
        utc: [
            'Europe/Kirov',
            'Europe/Moscow',
            'Europe/Simferopol',
            'Europe/Volgograd'
        ]
    },
    {
        value: 'Russia Time Zone 3',
        abbr: 'SAMT',
        offset: 4,
        isdst: false,
        text: stringResources.Timezone_SAMT,
        utc: [
            'Europe/Astrakhan',
            'Europe/Samara',
            'Europe/Ulyanovsk'
        ]
    },
    {
        value: 'Iran Standard Time',
        abbr: 'IDT',
        offset: 4.5,
        isdst: true,
        text: stringResources.Timezone_Iran,
        utc: [
            'Asia/Tehran'
        ]
    },
    {
        value: 'Arabian Standard Time',
        abbr: 'AST',
        offset: 4,
        isdst: false,
        text: stringResources.Timezone_Arabian,
        utc: [
            'Asia/Dubai',
            'Asia/Muscat',
            'Etc/GMT-4'
        ]
    },
    {
        value: 'Azerbaijan Standard Time',
        abbr: 'ADT',
        offset: 5,
        isdst: true,
        text: stringResources.Timezone_Azerbaijan,
        utc: [
            'Asia/Baku'
        ]
    },
    {
        value: 'Mauritius Standard Time',
        abbr: 'MST',
        offset: 4,
        isdst: false,
        text: stringResources.Timezone_Mauritius,
        utc: [
            'Indian/Mahe',
            'Indian/Mauritius',
            'Indian/Reunion'
        ]
    },
    {
        value: 'Georgian Standard Time',
        abbr: 'GST',
        offset: 4,
        isdst: false,
        text: stringResources.Timezone_Georgian,
        utc: [
            'Asia/Tbilisi'
        ]
    },
    {
        value: 'Caucasus Standard Time',
        abbr: 'CST',
        offset: 4,
        isdst: false,
        text: stringResources.Timezone_Caucasus,
        utc: [
            'Asia/Yerevan'
        ]
    },
    {
        value: 'Afghanistan Standard Time',
        abbr: 'AST',
        offset: 4.5,
        isdst: false,
        text: stringResources.Timezone_Afghan,
        utc: [
            'Asia/Kabul'
        ]
    },
    {
        value: 'West Asia Standard Time',
        abbr: 'WAST',
        offset: 5,
        isdst: false,
        text: stringResources.Timezone_WAST,
        utc: [
            'Antarctica/Mawson',
            'Asia/Aqtau',
            'Asia/Aqtobe',
            'Asia/Ashgabat',
            'Asia/Dushanbe',
            'Asia/Oral',
            'Asia/Samarkand',
            'Asia/Tashkent',
            'Etc/GMT-5',
            'Indian/Kerguelen',
            'Indian/Maldives'
        ]
    },
    {
        value: 'Pakistan Standard Time',
        abbr: 'PST',
        offset: 5,
        isdst: false,
        text: stringResources.Timezone_Pakistan,
        utc: [
            'Asia/Karachi'
        ]
    },
    {
        value: 'India Standard Time',
        abbr: 'IST',
        offset: 5.5,
        isdst: false,
        text: stringResources.Timezone_IST,
        utc: [
            'Asia/Kolkata',
            'Asia/Calcutta'
        ]
    },
    {
        value: 'Sri Lanka Standard Time',
        abbr: 'SLST',
        offset: 5.5,
        isdst: false,
        text: stringResources.Timezone_SLST,
        utc: [
            'Asia/Colombo'
        ]
    },
    {
        value: 'Nepal Standard Time',
        abbr: 'NST',
        offset: 5.75,
        isdst: false,
        text: stringResources.Timezone_Nepal,
        utc: [
            'Asia/Katmandu'
        ]
    },
    {
        value: 'Central Asia Standard Time',
        abbr: 'CAST',
        offset: 6,
        isdst: false,
        text: stringResources.Timezone_CAST,
        utc: [
            'Antarctica/Vostok',
            'Asia/Almaty',
            'Asia/Bishkek',
            'Asia/Qyzylorda',
            'Asia/Urumqi',
            'Etc/GMT-6',
            'Indian/Chagos'
        ]
    },
    {
        value: 'Bangladesh Standard Time',
        abbr: 'BST',
        offset: 6,
        isdst: false,
        text: stringResources.Timezone_Bangladesh,
        utc: [
            'Asia/Dhaka',
            'Asia/Thimphu'
        ]
    },
    {
        value: 'Ekaterinburg Standard Time',
        abbr: 'EST',
        offset: 5,
        isdst: false,
        text: stringResources.Timezone_Ekaterinburg,
        utc: [
            'Asia/Yekaterinburg'
        ]
    },
    {
        value: 'Myanmar Standard Time',
        abbr: 'MST',
        offset: 6.5,
        isdst: false,
        text: stringResources.Timezone_Myanmar,
        utc: [
            'Asia/Rangoon',
            'Indian/Cocos'
        ]
    },
    {
        value: 'SE Asia Standard Time',
        abbr: 'SAST',
        offset: 7,
        isdst: false,
        text: stringResources.Timezone_SEAsia,
        utc: [
            'Antarctica/Davis',
            'Asia/Bangkok',
            'Asia/Hovd',
            'Asia/Jakarta',
            'Asia/Phnom_Penh',
            'Asia/Pontianak',
            'Asia/Saigon',
            'Asia/Vientiane',
            'Etc/GMT-7',
            'Indian/Christmas'
        ]
    },
    {
        value: 'N. Central Asia Standard Time',
        abbr: 'NCAST',
        offset: 7,
        isdst: false,
        text: stringResources.Timezone_NCAST,
        utc: [
            'Asia/Novokuznetsk',
            'Asia/Novosibirsk',
            'Asia/Omsk'
        ]
    },
    {
        value: 'North Asia Standard Time',
        abbr: 'NAST',
        offset: 7,
        isdst: false,
        text: stringResources.Timezone_NAST,
        utc: [
            'Asia/Krasnoyarsk'
        ]
    },
    {
        value: 'China Standard Time',
        abbr: 'CST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_China,
        utc: [
            'Asia/Hong_Kong',
            'Asia/Macau',
            'Asia/Shanghai'
        ]
    },
    {
        value: 'Singapore Standard Time',
        abbr: 'MPST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_MPST,
        utc: [
            'Asia/Brunei',
            'Asia/Kuala_Lumpur',
            'Asia/Kuching',
            'Asia/Makassar',
            'Asia/Manila',
            'Asia/Singapore',
            'Etc/GMT-8'
        ]
    },
    {
        value: 'W. Australia Standard Time',
        abbr: 'WAST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_Perth,
        utc: [
            'Australia/Perth',
            'Antarctica/Casey'
        ]
    },
    {
        value: 'Taipei Standard Time',
        abbr: 'TST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_Taipei,
        utc: [
            'Asia/Taipei'
        ]
    },
    {
        value: 'Ulaanbaatar Standard Time',
        abbr: 'UST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_Ulaanbaatar,
        utc: [
            'Asia/Choibalsan',
            'Asia/Ulaanbaatar'
        ]
    },
    {
        value: 'North Asia East Standard Time',
        abbr: 'NAEST',
        offset: 8,
        isdst: false,
        text: stringResources.Timezone_NAEST,
        utc: [
            'Asia/Irkutsk'
        ]
    },
    {
        value: 'Tokyo Standard Time',
        abbr: 'TST',
        offset: 9,
        isdst: false,
        text: stringResources.Timezone_Tokyo,
        utc: [
            'Asia/Jayapura',
            'Asia/Tokyo',
            'Asia/Dili', // this one doesn't work in tz function
            'Etc/GMT-9',
            'Pacific/Palau'
        ]
    },
    {
        value: 'Korea Standard Time',
        abbr: 'KST',
        offset: 9,
        isdst: false,
        text: stringResources.Timezone_KST,
        utc: [
            'Asia/Pyongyang',
            'Asia/Seoul'
        ]
    },
    {
        value: 'Cen. Australia Standard Time',
        abbr: 'CAST',
        offset: 9.5,
        isdst: false,
        text: stringResources.Timezone_Adelaide,
        utc: [
            'Australia/Adelaide',
            'Australia/Broken_Hill'
        ]
    },
    {
        value: 'AUS Central Standard Time',
        abbr: 'ACST',
        offset: 9.5,
        isdst: false,
        text: stringResources.Timezone_ACST,
        utc: [
            'Australia/Darwin'
        ]
    },
    {
        value: 'E. Australia Standard Time',
        abbr: 'EAST',
        offset: 10,
        isdst: false,
        text: stringResources.Timezone_Brisbane,
        utc: [
            'Australia/Brisbane',
            'Australia/Lindeman'
        ]
    },
    {
        value: 'AUS Eastern Standard Time',
        abbr: 'AEST',
        offset: 11,
        isdst: true,
        dstSouth: true,
        text: stringResources.Timezone_AEST,
        utc: [
            'Australia/Melbourne',
            'Australia/Sydney'
        ]
    },
    {
        value: 'West Pacific Standard Time',
        abbr: 'WPST',
        offset: 10,
        isdst: false,
        text: stringResources.Timezone_WPST,
        utc: [
            'Antarctica/DumontDUrville',
            'Etc/GMT-10',
            'Pacific/Guam',
            'Pacific/Port_Moresby',
            'Pacific/Saipan',
            'Pacific/Truk'
        ]
    },
    {
        value: 'Tasmania Standard Time',
        abbr: 'TST',
        offset: 11,
        isdst: true,
        dstSouth: true,
        text: stringResources.Timezone_TST,
        utc: [
            'Australia/Currie',
            'Australia/Hobart'
        ]
    },
    {
        value: 'Yakutsk Standard Time',
        abbr: 'YST',
        offset: 9,
        isdst: false,
        text: stringResources.Timezone_YST,
        utc: [
            'Asia/Chita',
            'Asia/Khandyga',
            'Asia/Yakutsk'
        ]
    },
    {
        value: 'Central Pacific Standard Time',
        abbr: 'CPST',
        offset: 11,
        isdst: false,
        text: stringResources.Timezone_CPST,
        utc: [
            'Antarctica/Macquarie',
            'Etc/GMT-11',
            'Pacific/Efate',
            'Pacific/Guadalcanal',
            'Pacific/Kosrae',
            'Pacific/Noumea',
            'Pacific/Ponape'
        ]
    },
    {
        value: 'Vladivostok Standard Time',
        abbr: 'VST',
        offset: 10,
        isdst: false,
        text: stringResources.Timezone_Vladivostok,
        utc: [
            'Asia/Vladivostok',
            'Asia/Ust-Nera',
            'Asia/Sakhalin' // to do, this one is 11
        ]
    },
    {
        value: 'New Zealand Standard Time',
        abbr: 'NZST',
        offset: 13,
        isdst: true,
        dstSouth: true,
        text: stringResources.Timezone_NZST,
        utc: [
            'Antarctica/McMurdo',
            'Pacific/Auckland'
        ]
    },
    {
        value: 'UTC+12',
        abbr: 'U',
        offset: 12,
        isdst: false,
        text: stringResources.Timezone_UTC12,
        utc: [
            'Etc/GMT-12',
            'Pacific/Funafuti',
            'Pacific/Kwajalein',
            'Pacific/Majuro',
            'Pacific/Nauru',
            'Pacific/Tarawa',
            'Pacific/Wake',
            'Pacific/Wallis'
        ]
    },
    {
        value: 'Fiji Standard Time',
        abbr: 'FST',
        offset: 12,
        isdst: false,
        text: stringResources.Timezone_Fiji,
        utc: [
            'Pacific/Fiji'
        ]
    },
    {
        value: 'Magadan Standard Time',
        abbr: 'MST',
        offset: 11,
        isdst: false,
        text: stringResources.Timezone_Magadan,
        utc: [
            'Asia/Magadan',
            'Asia/Anadyr', // this one doesn't work in tz function
            'Asia/Kamchatka', // this one doesn't work in tz function
            'Asia/Srednekolymsk'
        ]
    },
    {
        value: 'Kamchatka Standard Time',
        abbr: 'KDT',
        offset: 13,
        isdst: true,
        text: stringResources.Timezone_KDT,
        utc: [
            'Asia/Kamchatka'
        ]
    },
    {
        value: 'Tonga Standard Time',
        abbr: 'TST',
        offset: 13,
        isdst: false,
        text: stringResources.Timezone_Tonga,
        utc: [
            'Etc/GMT-13',
            'Pacific/Enderbury',
            'Pacific/Fakaofo',
            'Pacific/Tongatapu'
        ]
    },
    {
        value: 'Samoa Standard Time',
        abbr: 'SST',
        offset: 13,
        isdst: false,
        text: stringResources.Timezone_SST,
        utc: [
            'Pacific/Samoa',
            'Pacific/Apia' // this one doesn't work in tz function
        ]
    }
];

/**
 * Return local timezone.
 * This function copy form src\Clients\PowerBIVisuals\VisualsCommon\Utility\Utility.ts.
 * This function uses summer and winter offset to determine local time zone.
 * The result localTimeZoneString must be a subset of the strings used by server,
 * as documented here: https://msdn.microsoft.com/en-us/library/gg154758.aspx (Dynamic Daylight Savings Time (Compact 2013)).
 * @return Local timezone string or UTC if timezone cannot be found.
 */
export const getLocalTimeZoneString = (): string => {
    const timeSummer = new Date(Date.UTC(2005, 6, 30, 0, 0, 0, 0));
    const summerOffset = -1 * timeSummer.getTimezoneOffset();
    const timeWinter = new Date(Date.UTC(2005, 12, 30, 0, 0, 0, 0));
    const winterOffset = -1 * timeWinter.getTimezoneOffset();
    let localTimeZoneString;

    if (-720 === summerOffset && -720 === winterOffset) { localTimeZoneString = 'Dateline Standard Time'; }
    else if (-660 === summerOffset && -660 === winterOffset) { localTimeZoneString = 'UTC-11'; }
    else if (-600 === summerOffset && -600 === winterOffset) { localTimeZoneString = 'Hawaiian Standard Time'; }
    else if (-480 === summerOffset && -540 === winterOffset) { localTimeZoneString = 'Alaskan Standard Time'; }
    else if (-420 === summerOffset && -480 === winterOffset) { localTimeZoneString = 'Pacific Standard Time'; }
    else if (-420 === summerOffset && -420 === winterOffset) { localTimeZoneString = 'US Mountain Standard Time'; }
    else if (-360 === summerOffset && -420 === winterOffset) { localTimeZoneString = 'Mountain Standard Time'; }
    else if (-360 === summerOffset && -360 === winterOffset) { localTimeZoneString = 'Central America Standard Time'; }
    else if (-300 === summerOffset && -360 === winterOffset) { localTimeZoneString = 'Central Standard Time'; }
    else if (-300 === summerOffset && -300 === winterOffset) { localTimeZoneString = 'SA Pacific Standard Time'; }
    else if (-240 === summerOffset && -300 === winterOffset) { localTimeZoneString = 'Eastern Standard Time'; }
    else if (-270 === summerOffset && -270 === winterOffset) { localTimeZoneString = 'Venezuela Standard Time'; }
    else if (-240 === summerOffset && -240 === winterOffset) { localTimeZoneString = 'SA Western Standard Time'; }
    else if (-240 === summerOffset && -180 === winterOffset) { localTimeZoneString = 'Central Brazilian Standard Time'; }
    else if (-180 === summerOffset && -240 === winterOffset) { localTimeZoneString = 'Atlantic Standard Time'; }
    else if (-180 === summerOffset && -180 === winterOffset) { localTimeZoneString = 'Montevideo Standard Time'; }
    else if (-180 === summerOffset && -120 === winterOffset) { localTimeZoneString = 'E. South America Standard Time'; }
    else if (-150 === summerOffset && -210 === winterOffset) { localTimeZoneString = 'Mid-Atlantic Standard Time'; }
    else if (-120 === summerOffset && -120 === winterOffset) { localTimeZoneString = 'SA Eastern Standard Time'; }
    else if (0 === summerOffset && 0 === winterOffset) { localTimeZoneString = 'UTC'; }
    else if (60 === summerOffset && 0 === winterOffset) { localTimeZoneString = 'GMT Standard Time'; }
    else if (60 === summerOffset && 120 === winterOffset) { localTimeZoneString = 'Namibia Standard Time'; }
    else if (120 === summerOffset && 60 === winterOffset) { localTimeZoneString = 'Romance Standard Time'; }
    else if (120 === summerOffset && 120 === winterOffset) { localTimeZoneString = 'South Africa Standard Time'; }
    else if (180 === summerOffset && 120 === winterOffset) { localTimeZoneString = 'GTB Standard Time'; }
    else if (180 === summerOffset && 180 === winterOffset) { localTimeZoneString = 'E. Africa Standard Time'; }
    else if (240 === summerOffset && 180 === winterOffset) { localTimeZoneString = 'Russian Standard Time'; }
    else if (240 === summerOffset && 240 === winterOffset) { localTimeZoneString = 'Arabian Standard Time'; }
    else if (270 === summerOffset && 210 === winterOffset) { localTimeZoneString = 'Iran Standard Time'; }
    else if (270 === summerOffset && 270 === winterOffset) { localTimeZoneString = 'Afghanistan Standard Time'; }
    else if (300 === summerOffset && 240 === winterOffset) { localTimeZoneString = 'Pakistan Standard Time'; }
    else if (300 === summerOffset && 300 === winterOffset) { localTimeZoneString = 'West Asia Standard Time'; }
    else if (330 === summerOffset && 330 === winterOffset) { localTimeZoneString = 'India Standard Time'; }
    else if (345 === summerOffset && 345 === winterOffset) { localTimeZoneString = 'Nepal Standard Time'; }
    else if (360 === summerOffset && 300 === winterOffset) { localTimeZoneString = 'N. Central Asia Standard Time'; }
    else if (360 === summerOffset && 360 === winterOffset) { localTimeZoneString = 'Central Asia Standard Time'; }
    else if (390 === summerOffset && 390 === winterOffset) { localTimeZoneString = 'Myanmar Standard Time'; }
    else if (420 === summerOffset && 360 === winterOffset) { localTimeZoneString = 'North Asia Standard Time'; }
    else if (420 === summerOffset && 420 === winterOffset) { localTimeZoneString = 'SE Asia Standard Time'; }
    else if (480 === summerOffset && 420 === winterOffset) { localTimeZoneString = 'North Asia East Standard Time'; }
    else if (480 === summerOffset && 480 === winterOffset) { localTimeZoneString = 'China Standard Time'; }
    else if (540 === summerOffset && 480 === winterOffset) { localTimeZoneString = 'Yakutsk Standard Time'; }
    else if (540 === summerOffset && 540 === winterOffset) { localTimeZoneString = 'Tokyo Standard Time'; }
    else if (570 === summerOffset && 570 === winterOffset) { localTimeZoneString = 'Cen. Australia Standard Time'; }
    else if (600 === summerOffset && 600 === winterOffset) { localTimeZoneString = 'E. Australia Standard Time'; }
    else if (600 === summerOffset && 660 === winterOffset) { localTimeZoneString = 'AUS Eastern Standard Time'; }
    else if (660 === summerOffset && 600 === winterOffset) { localTimeZoneString = 'Tasmania Standard Time'; }
    else if (660 === summerOffset && 660 === winterOffset) { localTimeZoneString = 'West Pacific Standard Time'; }
    else if (690 === summerOffset && 690 === winterOffset) { localTimeZoneString = 'Central Pacific Standard Time'; }
    else if (720 === summerOffset && 660 === winterOffset) { localTimeZoneString = 'Magadan Standard Time'; }
    else if (720 === summerOffset && 720 === winterOffset) { localTimeZoneString = 'Fiji Standard Time'; }
    else if (720 === summerOffset && 780 === winterOffset) { localTimeZoneString = 'New Zealand Standard Time'; }
    else if (780 === summerOffset && 780 === winterOffset) { localTimeZoneString = 'Tonga Standard Time'; }
    else { localTimeZoneString = 'UTC'; }
    return localTimeZoneString;
};

const timezoneKeyValue = new Map(timezones.map((timezone) => [timezone.value, timezone.text]));

if (stringResources) {
    timezoneKeyValue.set('US Eastern Standard Time', stringResources.Timezone_UEST);
}

export const fabricTimezones = [
    'Dateline Standard Time',
    'UTC-11',
    'Samoa Standard Time',
    'Hawaiian Standard Time',
    'Alaskan Standard Time',
    'Pacific Standard Time',
    'Pacific Standard Time (Mexico)',
    'Mountain Standard Time',
    'Mountain Standard Time (Mexico)',
    'US Mountain Standard Time',
    'Canada Central Standard Time',
    'Central America Standard Time',
    'Central Standard Time',
    'Central Standard Time (Mexico)',
    'Eastern Standard Time',
    'SA Pacific Standard Time',
    'US Eastern Standard Time',
    'Venezuela Standard Time',
    'Atlantic Standard Time',
    'Central Brazilian Standard Time',
    'Pacific SA Standard Time',
    'SA Western Standard Time',
    'Paraguay Standard Time',
    'Newfoundland Standard Time',
    'E. South America Standard Time',
    'Greenland Standard Time',
    'Montevideo Standard Time',
    'SA Eastern Standard Time',
    'Argentina Standard Time',
    'Mid-Atlantic Standard Time',
    'UTC-02',
    'Azores Standard Time',
    'Cape Verde Standard Time',
    'GMT Standard Time',
    'Greenwich Standard Time',
    'Morocco Standard Time',
    'UTC',
    'Central Europe Standard Time',
    'Central European Standard Time',
    'Romance Standard Time',
    'W. Central Africa Standard Time',
    'W. Europe Standard Time',
    'Namibia Standard Time',
    'E. Europe Standard Time',
    'Egypt Standard Time',
    'FLE Standard Time',
    'GTB Standard Time',
    'Israel Standard Time',
    'Jordan Standard Time',
    'Middle East Standard Time',
    'South Africa Standard Time',
    'Syria Standard Time',
    'Turkey Standard Time',
    'Arab Standard Time',
    'Arabic Standard Time',
    'E. Africa Standard Time',
    'Kaliningrad Standard Time',
    'Iran Standard Time',
    'Russian Standard Time',
    'Arabian Standard Time',
    'Azerbaijan Standard Time',
    'Caucasus Standard Time',
    'Georgian Standard Time',
    'Mauritius Standard Time',
    'Afghanistan Standard Time',
    'West Asia Standard Time',
    'Pakistan Standard Time',
    'India Standard Time',
    'Sri Lanka Standard Time',
    'Nepal Standard Time',
    'Ekaterinburg Standard Time',
    'Central Asia Standard Time',
    'Bangladesh Standard Time',
    'Myanmar Standard Time',
    'N. Central Asia Standard Time',
    'SE Asia Standard Time',
    'North Asia Standard Time',
    'China Standard Time',
    'Singapore Standard Time',
    'Taipei Standard Time',
    'W. Australia Standard Time',
    'Ulaanbaatar Standard Time',
    'North Asia East Standard Time',
    'Korea Standard Time',
    'Tokyo Standard Time',
    'AUS Central Standard Time',
    'Cen. Australia Standard Time',
    'Yakutsk Standard Time',
    'AUS Eastern Standard Time',
    'E. Australia Standard Time',
    'Tasmania Standard Time',
    'West Pacific Standard Time',
    'Vladivostok Standard Time',
    'Central Pacific Standard Time',
    'Magadan Standard Time',
    'Fiji Standard Time',
    'New Zealand Standard Time',
    'UTC+12',
    'Tonga Standard Time'
].map((zone) => ({ label: timezoneKeyValue.has(zone) ? timezoneKeyValue.get(zone) : zone, value: zone }));

export const getLocalISOString = (date: Date): string => {
    // Ensure date is a Date object
    if (!isDate(date)) {
        throw new Error('The provided value is not a Date object');
    }

    // Get the timezone offset in minutes
    const timezoneOffset = date.getTimezoneOffset();

    // Create a new Date object with the offset applied
    const localDate = new Date(date.getTime() - timezoneOffset * 60 * 1000);

    // Convert to ISO string without timezone information
    const isoString = localDate.toISOString();

    // Adjust the string to match local timezone
    // The date.toISOString() method returns the date in UTC, so we have to format it manually
    return isoString.replace('Z', '');
};
